import React, { useState, useEffect } from 'react';
import { 
    Target, Calendar, Edit, Save, Loader2, CheckCircle2, 
    AlertCircle, Search, TrendingUp, DollarSign, X, Check, Printer 
} from 'lucide-react';
import { getSalesTargetsProgress, saveSalesTarget, downloadSalesTargetsPdf } from '../../../../services/api/salesTargetApi';
import Card from '../../../../components/common/Card';
import Button from '../../../../components/common/Button';

const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-AE', {
        style: 'currency', currency: 'AED', minimumFractionDigits: 0, maximumFractionDigits: 2
    }).format(value || 0);
};

const SalesTargetsView = () => {
    const getCurrentMonthString = () => {
        const d = new Date();
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        return `${year}-${month}`;
    };

    const [month, setMonth] = useState(getCurrentMonthString());
    const [progressList, setProgressList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');

    // Modal state
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedSP, setSelectedSP] = useState(null);
    const [targetAmount, setTargetAmount] = useState('');
    const [saving, setSaving] = useState(false);
    const [successMessage, setSuccessMessage] = useState(null);
    const [downloadingPdf, setDownloadingPdf] = useState(false);

    const handleDownloadPdf = async () => {
        try {
            setDownloadingPdf(true);
            setError(null);
            await downloadSalesTargetsPdf(month);
        } catch (err) {
            console.error('Failed to download PDF:', err);
            setError('Failed to download Sales Target Report PDF.');
        } finally {
            setDownloadingPdf(false);
        }
    };

    const loadTargetsProgress = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await getSalesTargetsProgress(month);
            setProgressList(data || []);
        } catch (err) {
            console.error('[SalesTargetsView] Error loading target progress:', err);
            setError('Failed to load salesperson monthly targets progress.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadTargetsProgress();
    }, [month]);

    const handleOpenModal = (sp) => {
        setSelectedSP(sp);
        setTargetAmount(sp.targetAmount > 0 ? sp.targetAmount.toString() : '');
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setSelectedSP(null);
        setTargetAmount('');
        setIsModalOpen(false);
    };

    const handleSaveTarget = async (e) => {
        e.preventDefault();
        if (!selectedSP) return;

        try {
            setSaving(true);
            setError(null);
            
            const amount = parseFloat(targetAmount);
            if (isNaN(amount) || amount < 0) {
                setError('Please enter a valid positive target amount.');
                setSaving(false);
                return;
            }

            await saveSalesTarget({
                salesPersonPublicId: selectedSP.salesPersonPublicId,
                targetMonth: month,
                targetAmount: amount
            });

            setSuccessMessage(`Target updated for ${selectedSP.salesPersonName}!`);
            handleCloseModal();
            loadTargetsProgress();
            
            setTimeout(() => setSuccessMessage(null), 4000);
        } catch (err) {
            console.error('[SalesTargetsView] Error saving target:', err);
            setError('Failed to update sales target.');
        } finally {
            setSaving(false);
        }
    };

    const filteredList = progressList.filter(item => 
        item.salesPersonName?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Calculate month name
    const getMonthName = (monthStr) => {
        try {
            const [year, monthNum] = monthStr.split('-');
            const date = new Date(year, parseInt(monthNum) - 1, 1);
            return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
        } catch (e) {
            return monthStr;
        }
    };

    return (
        <div className="flex-1 flex flex-col min-h-0 bg-slate-50/50 dark:bg-slate-900/50 p-4 md:p-6 w-full max-w-[1280px] mx-auto">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 print:hidden">
                <div>
                    <h1 className="text-xl md:text-2xl font-black text-slate-800 dark:text-slate-100 flex items-center gap-2">
                        <Target className="w-6 h-6 text-cyan-600" />
                        Sales Target Dashboard
                    </h1>
                    <p className="text-xs text-slate-400 dark:text-slate-500 mt-1 font-medium">
                        Set and monitor monthly sales revenue performance for representatives.
                    </p>
                </div>

                {/* Filters Row */}
                <div className="flex flex-wrap items-center gap-3">
                    {/* Export PDF Button */}
                    <button
                        onClick={handleDownloadPdf}
                        disabled={downloadingPdf}
                        className="flex items-center gap-1.5 px-3.5 py-1.5 bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white dark:bg-slate-800 dark:hover:bg-blue-950/20 dark:hover:text-blue-400 border border-blue-100 rounded-xl text-xs font-black transition-all shadow-sm hover:shadow active:scale-95 cursor-pointer disabled:opacity-50"
                        title="Export PDF Report"
                    >
                        {downloadingPdf ? (
                            <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Exporting...
                            </>
                        ) : (
                            <>
                                <Printer className="w-4 h-4" />
                                Export PDF
                            </>
                        )}
                    </button>

                    {/* Month Picker */}
                    <div className="flex items-center gap-2 bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700/80 px-3 py-1.5 rounded-xl shadow-sm">
                        <Calendar className="w-4 h-4 text-cyan-600" />
                        <input
                            type="month"
                            value={month}
                            onChange={(e) => setMonth(e.target.value)}
                            className="bg-transparent border-none text-xs font-black text-slate-700 dark:text-slate-200 outline-none cursor-pointer"
                        />
                    </div>
                </div>
            </div>

            {/* Report Print Header - Visible Only on Print */}
            <div className="hidden print:flex flex-col gap-2 border-b-2 border-slate-200 pb-4 mb-6">
                <div className="flex items-center gap-2">
                    <Target className="w-6 h-6 text-cyan-600" />
                    <h1 className="text-2xl font-black text-slate-900">Sales Target Performance Report</h1>
                </div>
                <div className="flex justify-between items-center text-xs text-slate-500 font-bold">
                    <span>Target Month: {getMonthName(month)}</span>
                    <span>Generated On: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
                </div>
            </div>

            {successMessage && (
                <div className="flex items-center gap-3 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-100 dark:border-emerald-900/40 p-4 rounded-2xl shadow-sm text-emerald-800 dark:text-emerald-300 mb-4 animate-in fade-in slide-in-from-top-2 duration-200">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400 shrink-0" />
                    <p className="text-xs font-bold">{successMessage}</p>
                </div>
            )}

            {error && (
                <div className="flex items-center gap-3 bg-rose-50 dark:bg-rose-950/30 border border-rose-100 dark:border-rose-900/40 p-4 rounded-2xl shadow-sm text-rose-800 dark:text-rose-300 mb-4 animate-in fade-in slide-in-from-top-2 duration-200">
                    <AlertCircle className="w-5 h-5 text-rose-600 dark:text-rose-400 shrink-0" />
                    <p className="text-xs font-bold">{error}</p>
                </div>
            )}

            {/* List & Table Container */}
            <Card className="flex-1 flex flex-col min-h-0 border border-slate-100 dark:border-slate-800/80 p-0 overflow-hidden shadow-sm bg-white dark:bg-slate-800">
                {/* Search Bar */}
                <div className="p-4 border-b border-slate-100 dark:border-slate-700/50 flex items-center justify-between gap-4 print:hidden">
                    <div className="relative flex-1 max-w-md">
                        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <input
                            type="text"
                            placeholder="Search sales representative..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full bg-slate-50 dark:bg-slate-900/50 border border-slate-100 dark:border-slate-800 rounded-xl pl-10 pr-4 py-2 text-xs font-medium text-slate-800 dark:text-slate-100 placeholder-slate-400 outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500"
                        />
                    </div>
                    <div className="text-[11px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest bg-slate-50 dark:bg-slate-900/50 border border-slate-100 dark:border-slate-800 px-3 py-1.5 rounded-xl">
                        {getMonthName(month)}
                    </div>
                </div>

                {/* Table */}
                <div className="flex-1 overflow-auto">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-20 gap-3">
                            <Loader2 className="w-8 h-8 text-cyan-600 animate-spin" />
                            <p className="text-xs text-slate-400 font-bold uppercase tracking-wider">Evaluating representatives performance...</p>
                        </div>
                    ) : filteredList.length > 0 ? (
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-slate-50/70 dark:bg-slate-900/30 border-b border-slate-100 dark:border-slate-700/50">
                                    <th className="px-6 py-3.5 text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-wider">Representative Name</th>
                                    <th className="px-6 py-3.5 text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-wider text-right">Target Amount</th>
                                    <th className="px-6 py-3.5 text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-wider text-right">Achieved Amount</th>
                                    <th className="px-6 py-3.5 text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-wider">Target Progress</th>
                                    <th className="px-6 py-3.5 text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-wider text-center print:hidden">Action</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-700/50">
                                {filteredList.map((sp) => {
                                    const progressPct = parseFloat(sp.progressPercentage || 0);
                                    const isMet = sp.targetAmount > 0 && sp.achievedAmount >= sp.targetAmount;
                                    const progressBarColor = isMet 
                                        ? 'bg-emerald-500' 
                                        : progressPct >= 75 
                                            ? 'bg-cyan-500' 
                                            : progressPct >= 40 
                                                ? 'bg-amber-500' 
                                                : 'bg-rose-500';

                                    let displayPctStr = progressPct.toFixed(1);
                                    if (!isMet && parseFloat(displayPctStr) >= 100) {
                                        displayPctStr = "99.9";
                                    }

                                    return (
                                        <tr key={sp.salesPersonPublicId} className="hover:bg-slate-50/40 dark:hover:bg-slate-900/20 transition-colors">
                                            <td className="px-6 py-4.5">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-8 h-8 rounded-xl bg-cyan-50 dark:bg-cyan-950/20 text-cyan-600 flex items-center justify-center font-black text-xs border border-cyan-100/50 dark:border-cyan-900/30">
                                                        {sp.salesPersonName?.charAt(0) || 'S'}
                                                    </div>
                                                    <div className="min-w-0">
                                                        <p className="text-xs font-black text-slate-800 dark:text-slate-200 truncate">{sp.salesPersonName}</p>
                                                        <p className="text-[10px] text-slate-400 font-semibold tracking-wide mt-0.5">Sales Representative</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4.5 text-right font-black text-xs text-slate-700 dark:text-slate-300 tabular-nums">
                                                {sp.targetAmount > 0 ? formatCurrency(sp.targetAmount) : <span className="text-slate-400 font-medium">—</span>}
                                            </td>
                                            <td className="px-6 py-4.5 text-right font-black text-xs text-emerald-600 dark:text-emerald-400 tabular-nums">
                                                {formatCurrency(sp.achievedAmount)}
                                            </td>
                                            <td className="px-6 py-4.5 min-w-[200px]">
                                                <div className="flex flex-col gap-1.5 w-full max-w-xs">
                                                    <div className="flex items-center justify-between text-[10px] font-black text-slate-500">
                                                        <span>{displayPctStr}%</span>
                                                        {sp.targetAmount > 0 && sp.achievedAmount < sp.targetAmount && (
                                                            <span className="text-slate-400 font-bold uppercase tracking-wider text-[9px]">
                                                                {formatCurrency(sp.targetAmount - sp.achievedAmount)} to go
                                                            </span>
                                                        )}
                                                        {isMet && (
                                                            <span className="text-emerald-500 font-black uppercase tracking-wider text-[9px] flex items-center gap-0.5">
                                                                <Check className="w-3 h-3" /> Met
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="w-full h-2 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                                                        <div 
                                                            className={`h-full ${progressBarColor} rounded-full transition-all duration-500`}
                                                            style={{ width: `${Math.min(isMet ? 100 : 99.9, progressPct)}%` }}
                                                        />
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4.5 text-center print:hidden">
                                                <button
                                                    onClick={() => handleOpenModal(sp)}
                                                    className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-slate-100 hover:bg-cyan-50 hover:text-cyan-600 dark:bg-slate-800 dark:hover:bg-cyan-950/20 dark:hover:text-cyan-400 rounded-xl text-[10px] font-black text-slate-600 dark:text-slate-400 border border-transparent hover:border-cyan-100/50 dark:hover:border-cyan-900/30 transition-all cursor-pointer"
                                                >
                                                    <Edit className="w-3.5 h-3.5" />
                                                    Set Target
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    ) : (
                        <div className="flex flex-col items-center justify-center py-20 text-slate-400 gap-3">
                            <TrendingUp className="w-10 h-10 text-slate-300" />
                            <p className="text-sm font-bold">No representatives found</p>
                            <p className="text-xs">Add sales staff inside Keycloak first to set their monthly targets.</p>
                        </div>
                    )}
                </div>
            </Card>

            {/* Set Target Modal */}
            {isModalOpen && selectedSP && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700/80 rounded-3xl w-full max-w-md shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
                        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 dark:border-slate-700/50 bg-slate-50/50 dark:bg-slate-900/20">
                            <div className="flex items-center gap-2">
                                <Target className="w-5 h-5 text-cyan-600" />
                                <h3 className="text-sm font-black text-slate-800 dark:text-slate-100 uppercase tracking-wider">Set Monthly Target</h3>
                            </div>
                            <button 
                                onClick={handleCloseModal}
                                className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-xl text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors cursor-pointer"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        <form onSubmit={handleSaveTarget} className="p-6 flex flex-col gap-4">
                            {/* Representative Info */}
                            <div className="bg-slate-50 dark:bg-slate-900/50 p-4 rounded-2xl border border-slate-100 dark:border-slate-800 flex flex-col gap-2">
                                <div className="flex items-center justify-between text-xs">
                                    <span className="text-slate-400 font-semibold">Representative:</span>
                                    <span className="text-slate-800 dark:text-slate-200 font-extrabold">{selectedSP.salesPersonName}</span>
                                </div>
                                <div className="flex items-center justify-between text-xs">
                                    <span className="text-slate-400 font-semibold">Month:</span>
                                    <span className="text-cyan-600 dark:text-sky-400 font-extrabold">{getMonthName(month)}</span>
                                </div>
                            </div>

                            {/* Target Amount Input */}
                            <div className="flex flex-col gap-1.5">
                                <label className="text-xs font-black text-slate-500 uppercase tracking-wider px-1">Target Amount (AED)</label>
                                <div className="relative group">
                                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                                        AED
                                    </div>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        required
                                        placeholder="e.g. 25000.00"
                                        value={targetAmount}
                                        onChange={(e) => setTargetAmount(e.target.value)}
                                        className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl pl-12 pr-4 py-3 text-sm font-black text-slate-800 dark:text-slate-200 placeholder-slate-400 outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500 transition-all"
                                    />
                                </div>
                            </div>

                            {/* Actions */}
                            <div className="flex items-center gap-3 justify-end mt-4">
                                <Button
                                    type="button"
                                    variant="secondary"
                                    onClick={handleCloseModal}
                                    disabled={saving}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    variant="primary"
                                    disabled={saving}
                                    className="bg-cyan-500 text-white hover:bg-cyan-600 shadow-md shadow-cyan-500/20 font-black flex items-center gap-1.5"
                                >
                                    {saving ? (
                                        <>
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            Saving...
                                        </>
                                    ) : (
                                        <>
                                            <Save className="w-4 h-4" />
                                            Save Target
                                        </>
                                    )}
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Print Styling */}
            <style dangerouslySetInnerHTML={{ __html: `
                @media print {
                    @page {
                        size: A4 portrait;
                        margin: 1.5cm;
                    }
                    body {
                        background: white !important;
                        color: black !important;
                        -webkit-print-color-adjust: exact !important;
                        print-color-adjust: exact !important;
                    }
                    /* Hide sidebars, navbars, headers, filters, actions, search and other non-print components */
                    header, 
                    aside, 
                    nav,
                    .sidebar, 
                    .mobile-nav, 
                    #sidebar,
                    #mobile-navbar,
                    .print\\:hidden,
                    .print-hidden {
                        display: none !important;
                    }
                    /* Reset flex and overflows for standard layout container elements so it paginates correctly */
                    .flex-1, main, .min-h-screen, #root {
                        display: block !important;
                        height: auto !important;
                        min-height: 0 !important;
                        overflow: visible !important;
                        padding: 0 !important;
                        margin: 0 !important;
                    }
                    /* Ensure the main targets dashboard container takes full width */
                    .max-w-\\[1280px\\] {
                        max-width: 100% !important;
                        padding: 0 !important;
                        margin: 0 !important;
                    }
                    /* Remove shadow and borders/overflow-hidden on card wrapper */
                    .shadow-sm, .rounded-3xl, .overflow-hidden {
                        box-shadow: none !important;
                        overflow: visible !important;
                    }
                    /* Ink-saving styles for black and white printing - overrides dark backgrounds */
                    tfoot, 
                    .bg-slate-900,
                    .bg-slate-950,
                    .bg-gray-900,
                    .bg-black {
                        background-color: #f3f4f6 !important; /* light gray */
                        color: #111827 !important; /* dark text */
                    }
                    tfoot td, 
                    .bg-slate-900 *, 
                    .bg-slate-950 *, 
                    .bg-gray-900 *, 
                    .bg-black * {
                        color: #111827 !important;
                    }
                    /* Table adjustments for printing */
                    table {
                        width: 100% !important;
                        border-collapse: collapse !important;
                    }
                    th, td {
                        border-bottom: 1px solid #e2e8f0 !important;
                    }
                }
            ` }} />
        </div>
    );
};

export default SalesTargetsView;
